// stores/user.ts - Unified User Store with proper state management
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import axios from 'axios'
import { httpClient } from '../shared/services/http-client'

// Define types matching your backend response
export interface Permission {
  id?: number
  name?: string
  codename: string
}

export interface Group {
  id: number
  name: string
  permissions?: Permission[]
}

export interface Entity {
  id: number
  entity_name: string
  industry_id: number
  industry_name: string
}

export interface UserInfo {
  // Backend response fields
  entityLookUpList?: Entity[]
  label?: string
  specialPermission?: boolean
  specialPermissionList?: string[]
  permissionList?: { [entityId: string]: string[] } // Permissions per entity
  isSuperUser?: boolean
  isFindex?: boolean
  isFindexAll?: boolean
  lastSelectedEntity?: Entity
  krm_id?: number

  // MSAL/Azure AD fields
  oid?: string
  name?: string
  email?: string
  username?: string
  access_token?: string

  // Additional fields
  id?: number
  first_name?: string
  last_name?: string
  groups?: Group[]
  is_active?: boolean
  is_staff?: boolean
}

export const useUserStore = defineStore('user', () => {
  // State
  const user = ref<UserInfo | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isValidated = ref(false)
  const selectedEntityId = ref<number | null>(null)
  // Computed properties
  const isAuthenticated = computed(() => {
    return !!user.value?.access_token && isValidated.value
  })

  // Get all user permissions - combines special and entity-specific permissions
  const userPermissions = computed(() => {
    if (!user.value) return []

    const permissions = new Set<string>()

    // Add special permissions
    if (user.value.specialPermissionList) {
      user.value.specialPermissionList.forEach((p) => permissions.add(p))
    }

    // Add entity-specific permissions
    const entityId = selectedEntityId.value || user.value.lastSelectedEntity?.id
    if (entityId && user.value.permissionList) {
      const entityPerms = user.value.permissionList[String(entityId)] || []
      entityPerms.forEach((p) => permissions.add(p))
    }

    // Add implicit permissions for special users
    if (user.value.isSuperUser) {
      // Super users have all permissions
      permissions.add('admin_access')
      permissions.add('manage_users')
      permissions.add('view_all')
    }

    if (user.value.isFindex || user.value.isFindexAll) {
      permissions.add('view_finance')
      permissions.add('view_hats')
    }

    return Array.from(permissions)
  })

  // Get user groups
  const userGroups = computed(() => {
    return user.value?.groups || []
  })

  // Get display name for user
  const displayName = computed(() => {
    return user.value?.label || user.value?.name || user.value?.username || 'User'
  })

  // Get user role based on permissions
  const userRole = computed(() => {
    if (user.value?.isSuperUser) return 'Super Admin'
    if (user.value?.isFindex) return 'Findex User'
    if (user.value?.isFindexAll) return 'Findex All'

    const perms = userPermissions.value
    if (perms.includes('admin_access')) return 'Admin'
    // if (perms.includes('view_atls')) return 'ATLS Lead'
    // if (perms.includes('view_hats')) return 'HAT'
    if (perms.includes('view_krms')) return 'User'

    return 'User'
  })

  // Permission check methods
  const hasPermission = (permission: string): boolean => {
    if (user.value?.isSuperUser) return true
    return userPermissions.value.includes(permission)
  }

  const hasAnyPermission = (permissions: string[]): boolean => {
    if (user.value?.isSuperUser) return true
    return permissions.some((p) => userPermissions.value.includes(p))
  }

  const hasAllPermissions = (permissions: string[]): boolean => {
    if (user.value?.isSuperUser) return true
    return permissions.every((p) => userPermissions.value.includes(p))
  }

  const hasGroup = (groupName: string): boolean => {
    return userGroups.value.some((g) => g.name === groupName)
  }

  // Token management
  const setAccessToken = (token: string) => {
    sessionStorage.setItem('access_token', token)

    // Set token in axios defaults
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

    // Set token in httpClient
    if (httpClient && httpClient.defaults) {
      httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
    }
  }

  const getAccessToken = (): string | null => {
    return sessionStorage.getItem('access_token')
  }

  const removeAccessToken = () => {
    sessionStorage.removeItem('access_token')
    sessionStorage.removeItem('user_data')

    delete axios.defaults.headers.common['Authorization']

    if (httpClient && httpClient.defaults) {
      delete httpClient.defaults.headers.common['Authorization']
    }
  }

  // User management
  const updateUser = (userInfo: UserInfo | null) => {
    console.log('Updating user in store:', userInfo)
    user.value = userInfo

    if (userInfo) {
      // Store user data in session storage for persistence
      sessionStorage.setItem(
        'user_data',
        JSON.stringify({
          ...userInfo,
          access_token: undefined, // Don't store token in user data
        }),
      )

      if (userInfo.access_token) {
        setAccessToken(userInfo.access_token)
      }

      // Set selected entity
      if (userInfo.lastSelectedEntity) {
        selectedEntityId.value = userInfo.lastSelectedEntity.id
      }
    }
  }

  // Validate user with backend
  const validateUserWithBackend = async (accessToken: string, oid: string, email: string): Promise<UserInfo> => {
    isLoading.value = true
    error.value = null

    try {
      // Set the token first
      setAccessToken(accessToken)

      // Call your Django backend /users endpoint
      const response = await httpClient.get('/users')
      console.log('Backend response:', response.data)

      // The response matches your backend format
      const backendUser = response.data

      // Merge with existing user data
      const mergedUser: UserInfo = {
        ...user.value,
        ...backendUser,
        access_token: accessToken,
        oid: oid,
        email: email,
        username: email,
        name: backendUser.label || email,
      }

      updateUser(mergedUser)
      isValidated.value = true

      return mergedUser
    } catch (err: any) {
      console.error('Backend validation error:', err)
      error.value = err.response?.data?.message || 'Failed to validate user with backend'

      // Clear user on auth failure
      if (err.response?.status === 401 || err.response?.status === 403) {
        clearUser()
      }

      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Initialize from storage (for page refresh)
  const initializeFromStorage = async (): Promise<boolean> => {
    try {
      const token = getAccessToken()
      const storedUserData = sessionStorage.getItem('user_data')

      if (token && storedUserData) {
        // Restore user data from storage
        const userData = JSON.parse(storedUserData)

        // Set token in headers
        setAccessToken(token)

        // Try to refresh user data from backend
        try {
          const response = await httpClient.get('/users')
          const backendUser = response.data

          updateUser({
            ...userData,
            ...backendUser,
            access_token: token,
          })

          isValidated.value = true
          return true
        } catch (err) {
          // If backend fails but we have stored data, use it
          console.warn('Failed to refresh user data from backend, using cached data:', err)

          updateUser({
            ...userData,
            access_token: token,
          })

          isValidated.value = true
          return true
        }
      }

      return false
    } catch (err) {
      console.error('Failed to initialize user from storage:', err)
      clearUser()
      return false
    }
  }

  // Clear user data
  const clearUser = () => {
    user.value = null
    isValidated.value = false
    error.value = null
    selectedEntityId.value = null
    removeAccessToken()
  }

  // Refresh user data from backend
  const refreshUserData = async () => {
    if (!user.value?.access_token) {
      throw new Error('No access token available')
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await httpClient.get('/users')
      const userData = response.data

      updateUser({
        ...user.value,
        ...userData,
      })

      return userData
    } catch (err: any) {
      console.error('Failed to refresh user data:', err)
      error.value = err.response?.data?.message || 'Failed to refresh user data'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Switch entity
  const switchEntity = async (entityId: number) => {
    if (!user.value) return

    const entity = user.value.entityLookUpList?.find((e) => e.id === entityId)
    if (entity) {
      selectedEntityId.value = entityId

      // Update last selected entity
      updateUser({
        ...user.value,
        lastSelectedEntity: entity,
      })

      // Optionally call backend to persist entity selection
      try {
        await httpClient.post('/users/select-entity', { entity_id: entityId })
      } catch (err) {
        console.error('Failed to update entity selection on backend:', err)
      }
    }
  }

  return {
    // State
    user,
    isLoading,
    error,
    isValidated,
    selectedEntityId,

    // Computed
    isAuthenticated,
    userPermissions,
    userGroups,
    displayName,
    userRole,

    // Methods
    updateUser,
    clearUser,
    validateUserWithBackend,
    initializeFromStorage,
    refreshUserData,
    switchEntity,

    // Permission checks
    hasPermission,
    hasGroup,
    hasAnyPermission,
    hasAllPermissions,

    // Token management
    setAccessToken,
    getAccessToken,
    removeAccessToken,
  }
})
